import React from 'react';
import {
  Download,
  DollarSign,
  BarChart3,
  Store,
  FlaskConical,
  Repeat,
  ArrowUpRight,
} from 'lucide-react';

const pains = [
  {
    icon: Download,
    title: 'Installs are coming in, but revenue is not growing fast enough',
    body: 'You may be getting downloads, but trial starts, paid conversions, repeat purchases, or subscriptions are not improving at the same pace.',
  },
  {
    icon: BarChart3,
    title: 'Paid campaigns are running, but the numbers are hard to trust',
    body: 'Meta, Google, TikTok, Apple Search Ads, SKAN, GA4, Firebase, AppsFlyer, or Adjust may all show different versions of performance.',
  },
  {
    icon: Store,
    title: 'Your app-store page is not converting enough traffic',
    body: 'Ranking well is valuable, but weak screenshots, unclear messaging, poor reviews, or mismatched ad creative can quietly reduce install conversion.',
  },
  {
    icon: FlaskConical,
    title: 'Creative testing is too slow or too random',
    body: 'Many teams test ads, but not with a repeatable system that connects user insight, creative angles, landing/app-store pages, and revenue outcomes.',
  },
  {
    icon: Repeat,
    title: 'Growth depends too much on organic ranking',
    body: 'Organic visibility is great — until a competitor, seasonal change, or platform shift reduces momentum. Paid acquisition should create more predictable growth.',
  },
  {
    icon: DollarSign,
    title: 'You know the app can scale, but not which lever to pull first',
    body: 'Should you fix onboarding? Improve trial conversion? Test new audiences? Launch Apple Search Ads? Rebuild tracking? The sequence matters.',
  },
];

const Pain = () => {
  return (
    <section
      data-testid="app-growth-pain"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
            <ArrowUpRight size={16} /> The real problem
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
            Most apps do not have a traffic problem.{' '}
            <span className="text-blue-600">They have a growth-system problem.</span>
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Many app companies already have a strong product, loyal users, and visibility in the
            App Store or Google Play. But growth often gets stuck when acquisition, tracking,
            creative, onboarding, and monetization are not working together.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {pains.map((p, i) => {
            const Icon = p.icon;
            return (
              <div
                key={i}
                data-testid={`pain-card-${i}`}
                className="group bg-white border border-gray-100 rounded-2xl p-7 hover:border-blue-200 hover:shadow-xl transition-all duration-300"
              >
                <div className="w-11 h-11 rounded-xl bg-blue-50 text-blue-600 flex items-center justify-center mb-5 group-hover:bg-blue-500 group-hover:text-white transition-colors">
                  <Icon size={20} />
                </div>
                <h3 className="text-lg font-bold text-gray-900 leading-snug mb-3">
                  {p.title}
                </h3>
                <p className="text-gray-600 text-sm leading-relaxed">{p.body}</p>
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Pain;
